// ============================================================================
// PAYMENT SUCCESS PAGE - Email OTP Verification Flow (Fixed)
// ============================================================================

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, Mail, ArrowRight, Loader, KeyRound, AlertCircle } from 'lucide-react';
import { supabase } from '../lib/supabase';

const PaymentSuccess = () => {
  const navigate = useNavigate();

  // Flow states: 'success' -> 'email' -> 'otp' -> 'complete'
  const [step, setStep] = useState('success');
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [countdown, setCountdown] = useState(0);

  // Check if user is already logged in
  useEffect(() => {
    const checkAuth = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        console.log('User already logged in:', session.user.email);
        setStep('complete');
      }
    };
    checkAuth();
  }, []);

  // Countdown timer for resend
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  // Send OTP to email
  const handleSendOTP = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Always use shouldCreateUser: true to handle both new and existing users
      const { error } = await supabase.auth.signInWithOtp({
        email: email.toLowerCase(),
        options: {
          shouldCreateUser: true,
        }
      });

      if (error) throw error;

      console.log('OTP sent successfully to:', email);
      setStep('otp');
      setCountdown(60);
    } catch (err) {
      console.error('OTP send error:', err);
      setError(err.message || 'Failed to send verification code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Verify OTP - FIXED VERSION
  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      console.log('Verifying OTP for:', email);
      
      const { data, error } = await supabase.auth.verifyOtp({
        email: email.toLowerCase(),
        token: otp,
        type: 'email',
      });

      if (error) {
        console.error('OTP verification error:', error);
        throw error;
      }

      console.log('OTP verified successfully:', data);

      // Check if we have a user/session
      if (data?.user || data?.session) {
        console.log('User authenticated, proceeding to save data...');
        
        // Save profile and assessment data in background (non-blocking)
        saveUserData(data.user).catch(err => {
          console.warn('Background save failed (non-critical):', err);
        });

        // IMMEDIATELY redirect - don't wait for DB operations
        console.log('Redirecting to results...');
        setStep('complete');
        
        // Also try direct navigation as backup
        setTimeout(() => {
          window.location.href = '/results/full';
        }, 100);
        
        return;
      }

      // Fallback: Check if session exists anyway
      const { data: sessionData } = await supabase.auth.getSession();
      if (sessionData?.session) {
        console.log('Session found via fallback check');
        setStep('complete');
        setTimeout(() => {
          window.location.href = '/results/full';
        }, 100);
        return;
      }

      // If we get here, something went wrong
      throw new Error('Verification succeeded but no session was created. Please try again.');

    } catch (err) {
      console.error('OTP verify error:', err);
      setError(err.message || 'Invalid verification code. Please try again.');
      setLoading(false);
    }
  };

  // Save user data to database (runs in background, non-blocking)
  // FIXED: Updated to match actual database schema
  const saveUserData = async (user) => {
    if (!user) return;

    try {
      // Update/create profile with subscription status
      await supabase
        .from('profiles')
        .upsert({
          id: user.id,
          email: email.toLowerCase(),
          subscription_status: 'active',
          subscription_type: 'pro',
          subscription_start: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        }, { onConflict: 'id' });

      console.log('Profile updated successfully');

      // Save assessment results if they exist
      const storedResults = localStorage.getItem('lab2040_assessment_results');
      if (storedResults) {
        const results = JSON.parse(storedResults);
        const storedAnswers = localStorage.getItem('lab2040_assessment_answers');
        
        // FIXED: Match actual database schema (no 'results' column)
        await supabase.from('assessments').insert({
          user_id: user.id,
          answers: storedAnswers ? JSON.parse(storedAnswers) : {},
          user_type: results.userType || 'employee',
          composite_score: results.compositeScore,
          risk_level: results.riskLevel,
          industry_score: results.industryScore || null,
          profession_score: results.professionScore || null,
          knowledge_score: results.knowledgeScore || null,
          readiness_score: results.readinessScore || null,
          risk_score: results.riskScore || null,
          timeline: results.timeHorizon || null,
          key_risk_factors: results.keyRiskFactors || [],
          protective_factors: results.protectiveFactors || [],
          recommendations: results.recommendations || [],
          completed_at: new Date().toISOString(),
        });

        console.log('Assessment saved successfully');
        
        // Mark as saved to prevent duplicate saves
        localStorage.setItem('lab2040_assessment_saved', 'true');
      }
    } catch (err) {
      // Log but don't throw - this is non-critical
      console.warn('Error saving user data:', err);
    }
  };

  // Resend OTP
  const handleResendOTP = async () => {
    if (countdown > 0) return;

    setLoading(true);
    setError('');

    try {
      const { error } = await supabase.auth.signInWithOtp({
        email: email.toLowerCase(),
        options: {
          shouldCreateUser: true,
        }
      });

      if (error) throw error;
      console.log('OTP resent successfully');
      setCountdown(60);
    } catch (err) {
      console.error('Resend error:', err);
      setError(err.message || 'Failed to resend code.');
    } finally {
      setLoading(false);
    }
  };

  // Go to full report
  const handleViewReport = () => {
    window.location.href = '/results/full';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 to-cyan-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-8">

        {/* Step 1: Payment Success */}
        {step === 'success' && (
          <div className="text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <h1 className="font-serif text-3xl font-bold text-gray-900 mb-3">
              Payment Successful!
            </h1>
            <p className="text-gray-600 mb-8">
              Thank you for subscribing to Lab2040 Pro. Enter your email to access your full AI readiness report.
            </p>
            <button
              onClick={() => setStep('email')}
              className="w-full py-4 bg-gradient-to-r from-teal-600 to-cyan-600 text-white font-bold rounded-xl hover:shadow-lg transition-all flex items-center justify-center gap-2"
            >
              Continue
              <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        )}

        {/* Step 2: Enter Email */}
        {step === 'email' && (
          <div>
            <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Mail className="w-8 h-8 text-teal-600" />
            </div>
            <h2 className="font-serif text-2xl font-bold text-gray-900 text-center mb-2">
              Enter Your Email
            </h2>
            <p className="text-gray-600 text-center mb-6">
              We'll send you a verification code to access your report.
            </p>

            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700 text-sm">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                {error}
              </div>
            )}

            <form onSubmit={handleSendOTP}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none transition-all"
                />
              </div>
              <button
                type="submit"
                disabled={loading || !email}
                className="w-full py-4 bg-gradient-to-r from-teal-600 to-cyan-600 text-white font-bold rounded-xl hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <Loader className="w-5 h-5 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    Send Verification Code
                    <ArrowRight className="w-5 h-5" />
                  </>
                )}
              </button>
            </form>
          </div>
        )}

        {/* Step 3: Enter OTP */}
        {step === 'otp' && (
          <div>
            <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <KeyRound className="w-8 h-8 text-teal-600" />
            </div>
            <h2 className="font-serif text-2xl font-bold text-gray-900 text-center mb-2">
              Check Your Email
            </h2>
            <p className="text-gray-600 text-center mb-6">
              We sent a verification code to <strong>{email}</strong>
            </p>

            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700 text-sm">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                {error}
              </div>
            )}

            <form onSubmit={handleVerifyOTP}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Verification Code
                </label>
                <input
                  type="text"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="Enter 6-digit code"
                  required
                  maxLength={6}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none transition-all text-center text-2xl tracking-widest font-mono"
                />
                <p className="text-xs text-gray-500 mt-2 text-center">
                  Enter the 6-digit code from your email
                </p>
              </div>
              <button
                type="submit"
                disabled={loading || otp.length < 6}
                className="w-full py-4 bg-gradient-to-r from-teal-600 to-cyan-600 text-white font-bold rounded-xl hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <Loader className="w-5 h-5 animate-spin" />
                    Verifying...
                  </>
                ) : (
                  <>
                    Verify & Access Report
                    <ArrowRight className="w-5 h-5" />
                  </>
                )}
              </button>
            </form>

            <div className="mt-4 text-center">
              <button
                onClick={handleResendOTP}
                disabled={countdown > 0 || loading}
                className="text-teal-600 hover:text-teal-700 font-medium disabled:text-gray-400 disabled:cursor-not-allowed"
              >
                {countdown > 0 ? `Resend code in ${countdown}s` : 'Resend code'}
              </button>
            </div>

            <div className="mt-4 text-center">
              <button
                onClick={() => { setStep('email'); setOtp(''); setError(''); }}
                className="text-gray-500 hover:text-gray-700 text-sm"
              >
                ← Use a different email
              </button>
            </div>
          </div>
        )}

        {/* Step 4: Complete - Show Full Report */}
        {step === 'complete' && (
          <div className="text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <h1 className="font-serif text-3xl font-bold text-gray-900 mb-3">
              You're All Set!
            </h1>
            <p className="text-gray-600 mb-8">
              Your account is verified and your Pro subscription is active. View your complete AI readiness report now.
            </p>
            <button
              onClick={handleViewReport}
              className="w-full py-4 bg-gradient-to-r from-teal-600 to-cyan-600 text-white font-bold rounded-xl hover:shadow-lg transition-all flex items-center justify-center gap-2"
            >
              View Full Report
              <ArrowRight className="w-5 h-5" />
            </button>

            <p className="mt-6 text-sm text-gray-500">
              You can always access your reports from your dashboard.
            </p>
          </div>
        )}

      </div>
    </div>
  );
};

export default PaymentSuccess;
